/* eslint-disable no-restricted-syntax */
import { default as React, useState, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import CircularProgress from '@mui/material/CircularProgress';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, fetchApiRef } from '@backstage/core-plugin-api';

interface CloudProject {
  project_id: string;
  display_name: string;
  cloud: string;
  business_unit: string | null;
  owner: string | null;
  pillar: string | null;
  environment: string | null;
}

export const CloudProjectPicker = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  uiSchema,
}: FieldExtensionComponentProps<string>) => {
  const fetchApi = useApi(fetchApiRef);
  const cloudFilter = uiSchema?.['ui:options']?.cloud as string | undefined;

  const [projects, setProjects] = useState<CloudProject[] | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    const params = new URLSearchParams();
    if (cloudFilter) params.set('cloud', cloudFilter);
    fetchApi
      .fetch(`/api/cloud-inventory/projects?${params.toString()}`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch cloud projects');
        return res.json() as Promise<CloudProject[]>;
      })
      .then(data => {
        if (!cancelled) setProjects(data);
      })
      .catch(err => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fetchApi, cloudFilter]);

  const selected = projects?.find(p => p.project_id === formData) ?? null;

  return (
    <FormControl
      margin="normal"
      required={required}
      error={rawErrors?.length > 0}
      fullWidth
    >
      <Autocomplete<CloudProject>
        options={projects ?? []}
        loading={loading}
        value={selected}
        getOptionLabel={p =>
          p.display_name ? `${p.display_name} (${p.project_id})` : p.project_id
        }
        groupBy={p => p.pillar ?? p.cloud}
        isOptionEqualToValue={(a, b) => a.project_id === b.project_id}
        onChange={(_, value) => onChange(value?.project_id ?? '')}
        renderInput={params => (
          <TextField
            {...params}
            label={schema.title || 'Cloud Project'}
            required={required}
            error={rawErrors?.length > 0}
            placeholder="Search by project name or ID..."
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            InputLabelProps={{ shrink: true }}
          />
        )}
      />
      {error && (
        <FormHelperText error>
          Failed to load cloud projects. Please try again.
        </FormHelperText>
      )}
      {rawErrors?.length > 0 && (
        <FormHelperText error>{rawErrors.join(', ')}</FormHelperText>
      )}
      {schema.description && !error && !rawErrors?.length && (
        <FormHelperText>{schema.description}</FormHelperText>
      )}
    </FormControl>
  );
};

export const CloudProjectPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'CloudProjectPicker',
    component: CloudProjectPicker,
  }),
);
